/**
 * How tall the cookie bar may stand so it never sits over a control the page
 * needs while the notice is up (QA on PR #759: on a phone the bar covered the
 * /homes-for-sale/bend search field and took the tap meant for it).
 *
 * A page marks such a control with `data-cookie-bar-clear`. The bar's top
 * edge, plus whatever docks above it (the phone dock's --rr-dock-h, the sticky
 * ask's --rr-sticky-bottom), stays at or below the lowest marked box on
 * screen. The bar keeps every word: its content scrolls inside it, as it
 * already does past 85svh, with Decline and Accept all pinned to its foot.
 * Where a marked box leaves less than `minPx` under it the bar is left alone:
 * it never shrinks below the room that shows the ask.
 *
 * Pure. Tested in cookie-bar-fit.test.ts; wired in CookieConsentBanner.
 */

export const COOKIE_BAR_CLEAR_ATTR = 'data-cookie-bar-clear'

/** Heading line plus the Decline / Accept all row and the bar's padding. */
export const COOKIE_BAR_MIN_PX = 112

/** The bar's own class cap (max-h-[85svh]). */
const CLASS_CAP = 0.85

export type ClearBox = { top: number; bottom: number }

/**
 * The bar's max height in px, or null to keep its class cap. `keepClear` are
 * the marked boxes in viewport coordinates (getBoundingClientRect).
 */
export function cookieBarMaxHeight({
  viewportHeight,
  keepClear,
  stackedAbove = 0,
  minPx = COOKIE_BAR_MIN_PX,
}: {
  viewportHeight: number
  keepClear: readonly ClearBox[]
  stackedAbove?: number
  minPx?: number
}): number | null {
  // Off screen or not laid out (display: none measures 0 tall): nothing to clear.
  const onScreen = keepClear.filter((box) => box.bottom > box.top && box.bottom > 0 && box.top < viewportHeight)
  if (onScreen.length === 0) return null
  const lowest = Math.max(...onScreen.map((box) => box.bottom))
  const room = Math.floor(viewportHeight - lowest - Math.max(0, stackedAbove))
  if (room < minPx) return null
  if (room >= viewportHeight * CLASS_CAP) return null
  return room
}

/** A root custom property in px (`--rr-dock-h: 64px`), 0 when unset. */
export function rootPx(value: string | null | undefined): number {
  const n = Number.parseFloat(value ?? '')
  return Number.isFinite(n) && n > 0 ? n : 0
}
